import { markSessionExpired, clearSessionExpired } from "./sessionExpiry.js";

/**
 * Decides whether a 401 should send someone to the login screen.
 *
 * The naive version — "any 401 redirects" — is a trap by construction, and it is
 * how a user ends up stranded on the login page with no way back:
 *
 *   A page that does NOT require a session can still make a call that 401s. On
 *   the player lobby that is normal: it is browsable signed out, so a member
 *   endpoint answering "not signed in" is the correct answer, not a lapsed
 *   session. Redirecting on it throws the visitor off a public page, and if the
 *   same call runs again after the redirect, they never escape.
 *
 * So the rule is about the DESTINATION, not the response: only a route that
 * actually requires a session may be interrupted by one.
 *
 * Not redirecting is not the same as saying nothing. Whichever branch is taken,
 * a 401 that interrupts someone who believed they were signed in gets announced
 * — silently swallowing it is what made every button appear to stop working.
 *
 * @param {import("vue-router").Router} router
 * @param {object} [opts]
 * @param {string[]} [opts.neverRedirectFrom] route names that are already
 *        unauthenticated destinations — redirecting to login from them is a loop.
 * @param {() => boolean} [opts.hasSession] whether the app currently believes it
 *        is signed in. Called lazily at 401 time, so it can read a Pinia store
 *        that does not exist yet when this factory runs. Without it, an
 *        anonymous visitor's ordinary 401 would be reported as an expiry.
 * @param {() => void} [opts.onExpired] drop local user state. The cookie is
 *        already gone server-side; leaving the store populated keeps the app
 *        rendering a signed-in header over a session that no longer exists.
 */
export function createSessionRedirect(
  router, { neverRedirectFrom = [], hasSession = () => true, onExpired } = {}
) {
  const unauthenticated = new Set([
    "login", "register", "forgot", "reset", "verify-email",
    ...neverRedirectFrom,
  ]);

  let lastRedirectAt = 0;

  return () => {
    const route = router.currentRoute.value;

    // Nobody was signed in, so nothing expired. An anonymous visitor poking a
    // member endpoint gets the ordinary "not signed in" answer, and telling them
    // their session ran out would be a lie about a session they never had.
    if (!hasSession()) return;

    onExpired?.();

    // On a page that does not need a session, say so in place rather than
    // redirecting — but SAY it. This is the branch that used to be silent, and
    // silence is what made a dead session look like broken buttons.
    if (route.meta?.public || (route.name && unauthenticated.has(route.name))) {
      markSessionExpired();
      return;
    }

    // Several calls in flight when a session dies produce several 401s. Without
    // this they queue up redirects and the app thrashes.
    const now = Date.now();
    if (now - lastRedirectAt < 2000) return;
    lastRedirectAt = now;

    // No dialog on this branch. The login screen is about to render and reads
    // ?expired=1 itself — stacking a modal on top of a page that already
    // explains itself is two notices for one event.
    clearSessionExpired();

    router.push({
      name: "login",
      query: {
        expired: "1",
        // So the user lands back where they were once they sign in, rather than
        // being dumped at the top of the site.
        redirect: route.fullPath,
      },
    });
  };
}
